import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { getPaymentMode, formatNumberWithComma } from '../../utils/helpers';

// Helper function to get status name
const getStatusName = (status) => {
    return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
};

// Helper function to get customer name
const getCustomerName = (customerId, customerData) => {
    if (!customerData || !Array.isArray(customerData)) return 'Unknown';
    const customer = customerData.find(c => String(c.id) === String(customerId));
    return customer ? customer.customer_name : 'Unknown';
};

// Helper function to get branch name
const getBranchName = (branchId, branchData) => {
    if (!branchData || !Array.isArray(branchData)) return 'Unknown';
    const branch = branchData.find(b => String(b.id) === String(branchId));
    return branch ? `${branch.name}` : 'Unknown';
};

// Manual table drawing function (like drawOrdersTable in the sample)
const drawChairTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const rowHeight = 12;
    
    // Column widths for 10 columns: #, Number, Date, Customer, Cost, Qty, Total, Paid, Balance, Branch
    const colWidths = [15, 30, 35, 60, 25, 20, 30, 30, 30, 40]; // Total: 315
    
    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;
    
    if (currentY + neededHeight > pageHeight - 20) {
        doc.addPage('landscape');
        currentY = margin;
        
        // Redraw header on new page
        doc.setFillColor(25, 118, 210);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Branch'];
        let xPos = margin;
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        // Draw header underline
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }
    
    // Draw table header if not on new page
    if (startY === currentY) {
        doc.setFillColor(25, 118, 210);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Branch'];
        let xPos = margin;
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        // Draw header underline
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }
    
    // Draw table rows
    data.forEach((row, rowIndex) => {
        // Check for page break
        if (currentY + rowHeight > pageHeight - 20) {
            doc.addPage('landscape');
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            // Draw header background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Branch'];
            let xPos = margin;
            
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 8);
                xPos += colWidths[index];
            });
            
            // Draw header underline
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(0.5);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw row borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(margin, currentY, pageWidth - margin, currentY);
        
        // Draw row text
        let xPos = margin;
        row.forEach((cell, cellIndex) => {
            // Truncate text if too long
            let text = String(cell);
            if ((cellIndex === 3 || cellIndex === 9) && text.length > 25) { // Customer or Branch
                text = text.substring(0, 22) + '...';
            } else if (text.length > 15) {
                text = text.substring(0, 12) + '...';
            }
            
            doc.text(text, xPos + 2, currentY + 8);
            xPos += colWidths[cellIndex];
        });
        
        currentY += rowHeight;
    });
    
    return currentY;
};

// PDF export for Hiring Chair (following the sample structure)
export const exportToPDF = (chairs, branchData = [], customerData = [], filters = {}) => {
    try {
        const doc = new jsPDF('landscape');
        
        // Set document properties
        doc.setProperties({
            title: 'Hiring Chair Bookings Report',
            subject: 'Hiring Chair Bookings Export',
            author: 'Hotel Management System',
            keywords: 'chair, bookings, hiring, export, report',
            creator: 'Hotel Management System'
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('HIRING CHAIR BOOKINGS REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
        
        // Company/System name
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        doc.text('Hotel Management System', doc.internal.pageSize.width / 2, 35, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 45;
        
        // Report info in two columns
        const leftColumnX = 14;
        const rightColumnX = doc.internal.pageSize.width / 2 + 20;
        
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
        doc.text(`Total Records: ${chairs.length}`, rightColumnX, currentY);
        currentY += 8;
        
        // Calculate totals
        if (chairs.length > 0) {
            const totalAmount = chairs.reduce((sum, chair) => sum + parseFloat(chair.total_amount || 0), 0);
            const totalPaid = chairs.reduce((sum, chair) => sum + parseFloat(chair.paid_amount || 0), 0);
            const totalBalance = chairs.reduce((sum, chair) => sum + parseFloat(chair.balance_amount || 0), 0);
            
            doc.text(`Total Amount: ${formatNumberWithComma(totalAmount)}`, leftColumnX, currentY);
            doc.text(`Total Paid: ${formatNumberWithComma(totalPaid)}`, rightColumnX, currentY);
            currentY += 8;
            doc.text(`Total Balance: ${formatNumberWithComma(totalBalance)}`, leftColumnX, currentY);
            currentY += 8;
        }
        
        // Filters applied
        if (filters.customer_id || filters.payment_method || filters.dateFrom || filters.dateTo || filters.status || filters.branch_id || filters.check_in_from || filters.check_in_to) {
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.text('Applied Filters:', leftColumnX, currentY);
            currentY += 6;
            
            let filterLines = [];
            if (filters.customer_id) {
                const customer = customerData.find(c => String(c.id) === String(filters.customer_id));
                filterLines.push(`• Customer: ${customer ? customer.customer_name : 'Unknown'}`);
            }
            if (filters.payment_method) filterLines.push(`• Payment Method: ${getPaymentMode(filters.payment_method)}`);
            if (filters.branch_id) {
                const branch = branchData.find(b => String(b.id) === String(filters.branch_id));
                filterLines.push(`• Branch: ${branch ? branch.name : 'Unknown'}`);
            }
            if (filters.status) filterLines.push(`• Status: ${getStatusName(filters.status)}`);
            if (filters.dateFrom) filterLines.push(`• From date: ${filters.dateFrom}`);
            if (filters.dateTo) filterLines.push(`• To date: ${filters.dateTo}`);
            if (filters.check_in_from) filterLines.push(`• Check-in from: ${filters.check_in_from}`);
            if (filters.check_in_to) filterLines.push(`• Check-in to: ${filters.check_in_to}`);
            
            // Display filters in two columns if many
            const midPoint = Math.ceil(filterLines.length / 2);
            const leftFilters = filterLines.slice(0, midPoint);
            const rightFilters = filterLines.slice(midPoint);
            
            const maxLines = Math.max(leftFilters.length, rightFilters.length);
            
            for (let i = 0; i < maxLines; i++) {
                if (leftFilters[i]) {
                    doc.text(leftFilters[i], leftColumnX + 5, currentY);
                }
                if (rightFilters[i]) {
                    doc.text(rightFilters[i], rightColumnX, currentY);
                }
                currentY += 6;
            }
            currentY += 6;
        }
        
        // Prepare table data with selected columns: #, Number, Date, Customer, Cost, Qty, Total, Paid, Balance, Branch
        const tableData = chairs.map((chair, index) => [
            index + 1,
            chair.booking_no || '',
            formatDate(chair.created_at),
            getCustomerName(chair.customer_id, customerData),
            formatNumberWithComma(parseFloat(chair.cost || 0)),
            formatNumberWithComma(parseFloat(chair.quantity || 0)),
            formatNumberWithComma(parseFloat(chair.total_amount || 0)),
            formatNumberWithComma(parseFloat(chair.paid_amount || 0)),
            formatNumberWithComma(parseFloat(chair.balance_amount || 0)),
            getBranchName(chair.branch_id, branchData)
        ]);
        
        // Draw the table
        currentY = drawChairTable(doc, tableData, currentY + 10);
        
        // Add footer with page number
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
            doc.text(
                `Generated on: ${new Date().toLocaleDateString()}`,
                doc.internal.pageSize.width - 14,
                doc.internal.pageSize.height - 10,
                { align: 'right' }
            );
        }
        
        const fileName = `hiring_chair_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Hiring Chair PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// Excel export for Chair - Shows all attributes
export const exportToExcel = (chairs, branchData = [], customerData = [], filters = {}) => {
    try {
        // Prepare data for Excel with all attributes
        const excelData = chairs.map((chair, index) => ({
            '#': index + 1,
            'Booking ID': chair.id,
            'Booking Number': chair.booking_no || '',
            'Customer ID': chair.customer_id,
            'Customer Name': getCustomerName(chair.customer_id, customerData),
            'Customer Phone': getCustomerPhone(chair.customer_id, customerData),
            'Customer Email': getCustomerEmail(chair.customer_id, customerData),
            'Customer Address': chair.customer_address || '',
            'Additional Info': chair.additional_info || '',
            'Place Name': chair.place_name || '',
            'Cost per Unit': parseFloat(chair.cost || 0),
            'Quantity': parseFloat(chair.quantity || 0),
            'Total Amount': parseFloat(chair.total_amount || 0),
            'Paid Amount': parseFloat(chair.paid_amount || 0),
            'Balance Amount': parseFloat(chair.balance_amount || 0),
            'Payment Method': getPaymentMode(chair.payment_method),
            'Status': getStatusName(chair.status),
            'Branch': getBranchName(chair.branch_id, branchData),
            'Description': chair.description || '',
            'Check-in Date': chair.check_in_date || '',
            'Check-out Date': chair.check_out_date || '',
            'Created At': new Date(chair.created_at).toLocaleString(),
            'Updated At': new Date(chair.updated_at).toLocaleString()
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Hiring Chair Bookings');

        // Generate Excel file and download
        const fileName = `hiring_chair_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Hiring Chair Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// CSV export for Chair - Shows all attributes
export const exportToCSV = (chairs, branchData = [], customerData = []) => {
    try {
        const headers = [
            'Booking ID',
            'Booking Number',
            'Customer ID',
            'Customer Name',
            'Customer Phone',
            'Customer Email',
            'Customer Address',
            'Additional Info',
            'Place Name',
            'Cost per Unit',
            'Quantity',
            'Total Amount',
            'Paid Amount',
            'Balance Amount',
            'Payment Method',
            'Status',
            'Branch',
            'Description',
            'Check-in Date',
            'Check-out Date',
            'Created At',
            'Updated At'
        ];
        
        const csvData = chairs.map(chair => [
            chair.id,
            chair.booking_no || '',
            chair.customer_id,
            `"${getCustomerName(chair.customer_id, customerData).replace(/"/g, '""')}"`,
            `"${getCustomerPhone(chair.customer_id, customerData).replace(/"/g, '""')}"`,
            `"${getCustomerEmail(chair.customer_id, customerData).replace(/"/g, '""')}"`,
            `"${(chair.customer_address || '').replace(/"/g, '""')}"`,
            `"${(chair.additional_info || '').replace(/"/g, '""')}"`,
            `"${(chair.place_name || '').replace(/"/g, '""')}"`,
            parseFloat(chair.cost || 0).toFixed(2),
            parseFloat(chair.quantity || 0).toFixed(2),
            parseFloat(chair.total_amount || 0).toFixed(2),
            parseFloat(chair.paid_amount || 0).toFixed(2),
            parseFloat(chair.balance_amount || 0).toFixed(2),
            getPaymentMode(chair.payment_method),
            getStatusName(chair.status),
            `"${getBranchName(chair.branch_id, branchData).replace(/"/g, '""')}"`,
            `"${(chair.description || '').replace(/"/g, '""')}"`,
            `"${(chair.check_in_date || '').replace(/"/g, '""')}"`,
            `"${(chair.check_out_date || '').replace(/"/g, '""')}"`,
            `"${new Date(chair.created_at).toLocaleString()}"`,
            `"${new Date(chair.updated_at).toLocaleString()}"`
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `hiring_chair_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Hiring Chair CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};

// Helper function to get customer phone
const getCustomerPhone = (customerId, customerData) => {
    if (!customerData || !Array.isArray(customerData)) return '';
    const customer = customerData.find(c => String(c.id) === String(customerId));
    return customer ? customer.phone || '' : '';
};

// Helper function to get customer email
const getCustomerEmail = (customerId, customerData) => {
    if (!customerData || !Array.isArray(customerData)) return '';
    const customer = customerData.find(c => String(c.id) === String(customerId));
    return customer ? customer.email || '' : '';
};